'use client'

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'

// =============================================================================
// Supported Locales
// =============================================================================

export type Locale = 'en' | 'pt-BR'

// =============================================================================
// Canonical Product List — Bradesco Seguros
// =============================================================================

export const BRADESCO_PRODUCTS = [
  { id: 'odontologico', en: 'Dental Insurance', pt: 'Seg. Odontológico' },
  { id: 'viagem',       en: 'Travel Insurance', pt: 'Seg. Viagem' },
  { id: 'vida',         en: 'Life Insurance',   pt: 'Seg. Vida' },
  { id: 'automovel',    en: 'Auto Insurance',   pt: 'Seg. Automóvel' },
  { id: 'previdencia',  en: 'Pension Plan',      pt: 'Seg. Previdência' },
  { id: 'capitalizacao',en: 'Capitalization',    pt: 'Seg. Capitalização' },
] as const

/**
 * Maps any legacy/inconsistent product name to a canonical product id.
 * Used during the transition period to normalise old mock data references.
 */
export function normalizeProductId(raw: string): string {
  const lower = raw.toLowerCase().trim()
  const MAP: Record<string, string> = {
    'dental':                'odontologico',
    'dental insurance':      'odontologico',
    'odontológico':          'odontologico',
    'seg. odontológico':     'odontologico',
    'travel':                'viagem',
    'travel insurance':      'viagem',
    'viagem':                'viagem',
    'seg. viagem':           'viagem',
    'life':                  'vida',
    'life insurance':        'vida',
    'vida':                  'vida',
    'seg. vida':             'vida',
    'auto':                  'automovel',
    'auto insurance':        'automovel',
    'automóvel':             'automovel',
    'seg. automóvel':        'automovel',
    'pension':               'previdencia',
    'pension plan':          'previdencia',
    'previdência':           'previdencia',
    'seg. previdência':      'previdencia',
    'capitalização':         'capitalizacao',
    'capitalization':        'capitalizacao',
    'seg. capitalização':    'capitalizacao',
    // Non-official but kept in mock data
    'residential':           'residential',
    'residential insurance': 'residential',
    'consortium':            'consortium',
    'consórcio':             'consortium',
  }
  return MAP[lower] ?? lower
}

// =============================================================================
// Translation Dictionary
// =============================================================================

const translations: Record<Locale, Record<string, string>> = {
  en: {
    // ── Navigation ──────────────────────────────────────────────────────────
    'nav.platform':            'Platform',
    'nav.dashboard':           'Dashboard',
    'nav.workbench':           'Workbench',
    'nav.pivotTracker':        'Pivot Tracker',
    'nav.roi':                 'ROI & Attribution',
    'nav.dataManager':         'Data Manager',
    'nav.visitorIntel':        'Visitor Intelligence',
    'nav.aiIntelligence':      'AI Intelligence',
    'nav.agentHub':            'Agent Hub',
    'nav.aiPolicies':          'AI Policies',
    'nav.aiInsights':          'AI Insights',
    'nav.administration':      'Administration',
    'nav.userManagement':      'User Management',
    'nav.roleManagement':      'Role Management',
    'nav.groupManagement':     'Group Management',
    'nav.userSessions':        'User Sessions',
    'nav.loginEvents':         'Login Events',
    'nav.auditLogs':           'Audit Logs',
    'nav.adminSettings':       'Admin Settings',
    'nav.system':              'System',
    'nav.settings':            'Settings',

    // ── Header ──────────────────────────────────────────────────────────────
    'header.search':           'Search...',
    'header.aiManager':        'AI Manager',
    'header.profile':          'Profile',
    'header.settings':         'Settings',
    'header.signOut':          'Sign out',

    // ── Dashboard sections ──────────────────────────────────────────────────
    'dashboard.aiStatusBar':       'AI Status Bar',
    'dashboard.dailyBriefing':     'Daily Briefing',
    'dashboard.decisionQueue':     'Decision Queue',
    'dashboard.conversionFunnel':  'Conversion Funnel',
    'dashboard.funnelStages':      'Funnel Stages',
    'dashboard.workforcePulse':    'Workforce Pulse',
    'dashboard.greeting':          'Good morning',

    // ── Funnel / Product Cards ──────────────────────────────────────────────
    'funnel.visitors':             'visitors',
    'funnel.conversions':          'conversions',
    'funnel.vsBaseline':           'vs baseline',
    'funnel.pageVisitors':         'Page Visitors',
    'funnel.aiEngaged':            'AI Engaged',
    'funnel.quoteStarted':         'Quote Started',
    'funnel.domainTransition':     'Domain Transition',
    'funnel.quoteCompleted':       'Quote Completed',
    'funnel.policyIssued':         'Policy Issued',

    // ── Workbench ───────────────────────────────────────────────────────────
    'workbench.title':             'Lead Pipeline',
    'workbench.new':               'New',
    'workbench.engaged':           'Engaged',
    'workbench.qualified':         'Qualified',
    'workbench.nurturing':         'Nurturing',
    'workbench.converting':        'Converting',
    'workbench.wonLost':           'Won/Lost',

    // ── Filters ─────────────────────────────────────────────────────────────
    'filter.allProducts':          'All Products',
    'filter.allClassifications':   'All Classifications',
    'filter.allSegments':          'All Segments',
    'filter.allAccounts':          'All Accounts',
    'filter.allChannels':          'All Channels',
    'filter.searchPlaceholder':    'Search name or ID...',
    'filter.clearFilters':         'Clear Filters',
    'filter.hot':                  'Hot',
    'filter.warm':                 'Warm',
    'filter.cool':                 'Cool',
    'filter.correntista':          'Account Holder',
    'filter.naoCorrentista':       'Non-Account Holder',
    'filter.today':                'Today',

    // ── Buttons / Actions ───────────────────────────────────────────────────
    'action.simulate':             'Simulate',
    'action.simulating':           'Simulating...',
    'action.clear':                'Clear',
    'action.close':                'Close',
    'action.signIn':               'Sign In with SSO',
    'action.collapse':             'Collapse',
    'action.expand':               'Expand sidebar',

    // ── Metrics ─────────────────────────────────────────────────────────────
    'metric.activeLeads':          'Active Leads',
    'metric.conversionRate':       'Conversion Rate',
    'metric.revenueMTD':           'Revenue MTD',
    'metric.avgResponseTime':      'Avg Response Time',
    'metric.brokerAcceptance':     'Broker Acceptance',
    'metric.lgpdCompliance':       'LGPD Compliance',
    'metric.activeAgents':         'Active Agents',
    'metric.visitorsProcessing':   'Visitors Processing',
    'metric.leadsScored':          'Leads Scored',
    'metric.policiesIssued':       'Policies Issued',
    'metric.systemUptime':         'System Uptime',

    // ── ROI ─────────────────────────────────────────────────────────────────
    'roi.title':                   'ROI & Attribution',
    'roi.revenueByProduct':        'Revenue by Product',
    'roi.monthlyRevenue':          'Monthly Revenue',
    'roi.costPerAcquisition':      'Cost per Acquisition',
    'roi.policiesMonth':           'Policies/Month',

    // ── Insights ────────────────────────────────────────────────────────────
    'insights.title':              'AI Insights',
    'insights.showing':            'Showing',
    'insights.of':                 'of',
    'insights.insights':           'insights',
    'insights.severity':           'Severity',
    'insights.category':           'Category',
    'insights.status':             'Status',
    'insights.agent':              'Agent',
    'insights.sort':               'Sort',
    'insights.impact':             'Impact',
    'insights.recency':            'Recency',
    'insights.all':                'All',
    'insights.high':               'High',
    'insights.medium':             'Medium',
    'insights.low':                'Low',
    'insights.pending':            'Pending',
    'insights.accepted':           'Accepted',
    'insights.autoApplied':        'Auto-Applied',
    'insights.dismissed':          'Dismissed',

    // ── Policies ────────────────────────────────────────────────────────────
    'policies.title':              'AI Policies',
    'policies.simulator':          'Simulator',
    'policies.product':            'Product',
    'policies.segment':            'Segment',
    'policies.behavior':           'Behavior',
    'policies.select':             'Select...',
    'policies.ruleFlow':           'Rule Flow',
    'policies.rulesFired':         'rules fired',

    // ── Pivot ───────────────────────────────────────────────────────────────
    'pivot.title':                 'Pivot Tracker',

    // ── Data Manager ────────────────────────────────────────────────────────
    'dataManager.title':           'Data Manager',

    // ── Auth ────────────────────────────────────────────────────────────────
    'auth.pendingTitle':           'Account Pending Approval',
    'auth.welcome':                'Welcome',
    'auth.commandCenterAccess':    'Command Center Access',

    // ── AI Status Bar ──────────────────────────────────────────────────────
    'status.aiEmployeesActive':    'AI Employees Active',
    'status.processing':           'Processing',
    'status.visitors':             'visitors',
    'status.leadsScored':          'Leads Scored',
    'status.policiesIssued':       'Policies Issued',
    'status.uptime':               'Uptime',
    'status.lastSync':             'Last sync',
    'status.live':                 'live',
    'status.liveVisitors':         'live visitors',
    'status.sessionsToday':        'sessions today',
    'status.eventsTracked':        'events tracked',

    // ── Daily Briefing ─────────────────────────────────────────────────────
    'briefing.title':              'Command Briefing',
    'briefing.generatedBy':        'Generated by',
    'briefing.goodMorning':        'Good morning',

    // ── Decision Queue ─────────────────────────────────────────────────────
    'decision.title':              'Decisions Awaiting Review',
    'decision.noPending':          'No decisions pending — all clear ✓',
    'decision.immediate':          'Immediate',
    'decision.today':              'Today',
    'decision.thisWeek':           'This Week',

    // ── Leak Alerts ────────────────────────────────────────────────────────
    'leakAlerts.title':            'Leak Alerts',

    // ── Products (English labels) ───────────────────────────────────────────
    'product.odontologico':        'Dental Insurance',
    'product.viagem':              'Travel Insurance',
    'product.vida':                'Life Insurance',
    'product.automovel':           'Auto Insurance',
    'product.previdencia':         'Pension Plan',
    'product.capitalizacao':       'Capitalization',
    'product.residential':         'Residential Insurance',
    'product.consortium':          'Consortium',
  },

  'pt-BR': {
    // ── Navegação ───────────────────────────────────────────────────────────
    'nav.platform':            'Plataforma',
    'nav.dashboard':           'Painel',
    'nav.workbench':           'Bancada',
    'nav.pivotTracker':        'Rastreador de Pivô',
    'nav.roi':                 'ROI e Atribuição',
    'nav.dataManager':         'Gestão de Dados',
    'nav.visitorIntel':        'Inteligência de Visitantes',
    'nav.aiIntelligence':      'Inteligência IA',
    'nav.agentHub':            'Central de Agentes',
    'nav.aiPolicies':          'Políticas de IA',
    'nav.aiInsights':          'Insights de IA',
    'nav.administration':      'Administração',
    'nav.userManagement':      'Gestão de Usuários',
    'nav.roleManagement':      'Gestão de Perfis',
    'nav.groupManagement':     'Gestão de Grupos',
    'nav.userSessions':        'Sessões de Usuários',
    'nav.loginEvents':         'Eventos de Login',
    'nav.auditLogs':           'Logs de Auditoria',
    'nav.adminSettings':       'Config. Administrativas',
    'nav.system':              'Sistema',
    'nav.settings':            'Configurações',

    // ── Cabeçalho ───────────────────────────────────────────────────────────
    'header.search':           'Buscar...',
    'header.aiManager':        'Gerente IA',
    'header.profile':          'Perfil',
    'header.settings':         'Configurações',
    'header.signOut':          'Sair',

    // ── Seções do Painel ────────────────────────────────────────────────────
    'dashboard.aiStatusBar':       'Status da IA',
    'dashboard.dailyBriefing':     'Briefing Diário',
    'dashboard.decisionQueue':     'Fila de Decisões',
    'dashboard.conversionFunnel':  'Funil de Conversão',
    'dashboard.funnelStages':      'Etapas do Funil',
    'dashboard.workforcePulse':    'Pulso da Equipe',
    'dashboard.greeting':          'Bom dia',

    // ── Funil / Cards de Produto ────────────────────────────────────────────
    'funnel.visitors':             'visitantes',
    'funnel.conversions':          'conversões',
    'funnel.vsBaseline':           'vs linha de base',
    'funnel.pageVisitors':         'Visitantes da Página',
    'funnel.aiEngaged':            'IA Engajou',
    'funnel.quoteStarted':         'Cotação Iniciada',
    'funnel.domainTransition':     'Transição de Domínio',
    'funnel.quoteCompleted':       'Cotação Concluída',
    'funnel.policyIssued':         'Apólice Emitida',

    // ── Bancada ─────────────────────────────────────────────────────────────
    'workbench.title':             'Pipeline de Leads',
    'workbench.new':               'Novo',
    'workbench.engaged':           'Engajado',
    'workbench.qualified':         'Qualificado',
    'workbench.nurturing':         'Em Nutrição',
    'workbench.converting':        'Convertendo',
    'workbench.wonLost':           'Ganho/Perdido',

    // ── Filtros ─────────────────────────────────────────────────────────────
    'filter.allProducts':          'Todos os Produtos',
    'filter.allClassifications':   'Todas as Classificações',
    'filter.allSegments':          'Todos os Segmentos',
    'filter.allAccounts':          'Todas as Contas',
    'filter.allChannels':          'Todos os Canais',
    'filter.searchPlaceholder':    'Buscar nome ou ID...',
    'filter.clearFilters':         'Limpar Filtros',
    'filter.hot':                  'Quente',
    'filter.warm':                 'Morno',
    'filter.cool':                 'Frio',
    'filter.correntista':          'Correntista',
    'filter.naoCorrentista':       'Não-Correntista',
    'filter.today':                'Hoje',

    // ── Botões / Ações ──────────────────────────────────────────────────────
    'action.simulate':             'Simular',
    'action.simulating':           'Simulando...',
    'action.clear':                'Limpar',
    'action.close':                'Fechar',
    'action.signIn':               'Entrar com SSO',
    'action.collapse':             'Recolher',
    'action.expand':               'Expandir barra lateral',

    // ── Métricas ────────────────────────────────────────────────────────────
    'metric.activeLeads':          'Leads Ativos',
    'metric.conversionRate':       'Taxa de Conversão',
    'metric.revenueMTD':           'Receita MTD',
    'metric.avgResponseTime':      'Tempo Médio de Resposta',
    'metric.brokerAcceptance':     'Aceite do Corretor',
    'metric.lgpdCompliance':       'Conformidade LGPD',
    'metric.activeAgents':         'Agentes Ativos',
    'metric.visitorsProcessing':   'Visitantes em Processamento',
    'metric.leadsScored':          'Leads Qualificados',
    'metric.policiesIssued':       'Apólices Emitidas',
    'metric.systemUptime':         'Disponibilidade do Sistema',

    // ── ROI ─────────────────────────────────────────────────────────────────
    'roi.title':                   'ROI e Atribuição',
    'roi.revenueByProduct':        'Receita por Produto',
    'roi.monthlyRevenue':          'Receita Mensal',
    'roi.costPerAcquisition':      'Custo por Aquisição',
    'roi.policiesMonth':           'Apólices/Mês',

    // ── Insights ────────────────────────────────────────────────────────────
    'insights.title':              'Insights de IA',
    'insights.showing':            'Mostrando',
    'insights.of':                 'de',
    'insights.insights':           'insights',
    'insights.severity':           'Severidade',
    'insights.category':           'Categoria',
    'insights.status':             'Status',
    'insights.agent':              'Agente',
    'insights.sort':               'Ordenar',
    'insights.impact':             'Impacto',
    'insights.recency':            'Recência',
    'insights.all':                'Todos',
    'insights.high':               'Alta',
    'insights.medium':             'Média',
    'insights.low':                'Baixa',
    'insights.pending':            'Pendente',
    'insights.accepted':           'Aceito',
    'insights.autoApplied':        'Auto-Aplicado',
    'insights.dismissed':          'Descartado',

    // ── Políticas ───────────────────────────────────────────────────────────
    'policies.title':              'Políticas de IA',
    'policies.simulator':          'Simulador',
    'policies.product':            'Produto',
    'policies.segment':            'Segmento',
    'policies.behavior':           'Comportamento',
    'policies.select':             'Selecionar...',
    'policies.ruleFlow':           'Fluxo de Regras',
    'policies.rulesFired':         'regras ativadas',

    // ── Pivô ────────────────────────────────────────────────────────────────
    'pivot.title':                 'Rastreador de Pivô',

    // ── Gestão de Dados ─────────────────────────────────────────────────────
    'dataManager.title':           'Gestão de Dados',

    // ── Autenticação ────────────────────────────────────────────────────────
    'auth.pendingTitle':           'Conta Pendente de Aprovação',
    'auth.welcome':                'Bem-vindo',
    'auth.commandCenterAccess':    'Acesso ao Centro de Comando',

    // ── Barra de Status da IA ──────────────────────────────────────────────
    'status.aiEmployeesActive':    'Agentes de IA Ativos',
    'status.processing':           'Processando',
    'status.visitors':             'visitantes',
    'status.leadsScored':          'Leads Qualificados',
    'status.policiesIssued':       'Apólices Emitidas',
    'status.uptime':               'Disponibilidade',
    'status.lastSync':             'Última sinc.',
    'status.live':                 'ao vivo',
    'status.liveVisitors':         'visitantes ao vivo',
    'status.sessionsToday':        'sessões hoje',
    'status.eventsTracked':        'eventos rastreados',

    // ── Briefing Diário ────────────────────────────────────────────────────
    'briefing.title':              'Briefing de Comando',
    'briefing.generatedBy':        'Gerado por',
    'briefing.goodMorning':        'Bom dia',

    // ── Fila de Decisões ───────────────────────────────────────────────────
    'decision.title':              'Decisões Aguardando Revisão',
    'decision.noPending':          'Nenhuma decisão pendente — tudo certo ✓',
    'decision.immediate':          'Imediato',
    'decision.today':              'Hoje',
    'decision.thisWeek':           'Esta Semana',

    // ── Alertas de Vazamento ───────────────────────────────────────────────
    'leakAlerts.title':            'Alertas de Vazamento',

    // ── Produtos (rótulos PT-BR) ────────────────────────────────────────────
    'product.odontologico':        'Seg. Odontológico',
    'product.viagem':              'Seg. Viagem',
    'product.vida':                'Seg. Vida',
    'product.automovel':           'Seg. Automóvel',
    'product.previdencia':         'Seg. Previdência',
    'product.capitalizacao':       'Seg. Capitalização',
    'product.residential':         'Seg. Residencial',
    'product.consortium':          'Consórcio',
  },
}

// =============================================================================
// Context
// =============================================================================

interface LanguageContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
  /** Translate a product id (e.g. 'odontologico') to its display name */
  tp: (productId: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const STORAGE_KEY = 'bradesco-cc-locale'

// =============================================================================
// Provider
// =============================================================================

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('pt-BR')

  // Read persisted locale on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as Locale | null
      if (stored === 'en' || stored === 'pt-BR') {
        setLocaleState(stored)
      }
    } catch {
      // localStorage unavailable (SSR, private browsing)
    }
  }, [])

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale)
    try {
      localStorage.setItem(STORAGE_KEY, newLocale)
    } catch {
      // Silently fail
    }
    // Update the html lang attribute
    document.documentElement.lang = newLocale === 'pt-BR' ? 'pt-BR' : 'en'
  }, [])

  const t = useCallback(
    (key: string): string => {
      return translations[locale]?.[key] ?? translations['en']?.[key] ?? key
    },
    [locale]
  )

  const tp = useCallback(
    (productId: string): string => {
      const key = `product.${productId}`
      return translations[locale]?.[key] ?? translations['en']?.[key] ?? productId
    },
    [locale]
  )

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t, tp }}>
      {children}
    </LanguageContext.Provider>
  )
}

// =============================================================================
// Hook
// =============================================================================

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
